/**
 * RecurrenceValue ⇄ cron string compiler.
 *
 * The form mode of the schedule cadence picker (CronPicker) reuses the same
 * meeting-style RecurrenceEditor in `mode="cron"`. These helpers translate
 * between the form's `RecurrenceValue` shape and a 5-field cron expression.
 *
 * Form mode supports the simple recurring patterns the legacy cadence enum
 * supported (every-N-minutes, every-N-hours, daily, weekly, monthly,
 * yearly, monthly-last-day). Cron expressions outside this subset are
 * "advanced-only" and won't round-trip into form mode.
 */

import type { RecurrenceValue } from "../components/RecurrenceEditor.js";
import { EMPTY_RECURRENCE } from "../components/RecurrenceEditor.js";

const DAY_CODE_TO_NUM: Record<string, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};
const DAY_NUM_TO_CODE = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

function parseHHMM(t: string): [number, number] {
  const [h = "0", m = "0"] = t.split(":");
  return [parseInt(h, 10), parseInt(m, 10)];
}

function dayOfMonthFromDate(d: string): number {
  return parseInt(d.split("-")[2] ?? "1", 10);
}

function monthFromDate(d: string): number {
  return parseInt(d.split("-")[1] ?? "1", 10);
}

function dayCodeFromDateStr(d: string): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, day ?? 1, 12, 0, 0));
  return ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][dt.getUTCDay()] ?? "MO";
}

// ---------------------------------------------------------------------------
// RecurrenceValue → cron
// ---------------------------------------------------------------------------

export function compileRecurrenceToCron(v: RecurrenceValue): string {
  const [hour, minute] = parseHHMM(v.all_day ? "00:00" : v.start_time);

  if (!v.is_recurring) {
    // Form mode doesn't express one-time fires (cron is by definition recurring);
    // fall through to a "daily at start_time" baseline. Callers that need a
    // one-time schedule must use Advanced mode.
    return `${minute} ${hour} * * *`;
  }

  switch (v.granularity) {
    case "minutely": {
      // Anchored to start_time's minute. "Every 30 starting 9:15" → fires at
      // :15 and :45 of every hour (cron `15/30 * * * *`).
      const n = Math.max(1, v.interval);
      const startMin = parseHHMM(v.start_time)[1];
      if (n === 1) return `* * * * *`;
      // M=0 + N divides 60 → cleaner `*/N` form
      if (startMin === 0 && 60 % n === 0) return `*/${n} * * * *`;
      return `${startMin}/${n} * * * *`;
    }
    case "hourly": {
      // Anchored to start_time. "Every 4 starting 14:15" → fires at 14:15,
      // 18:15, 22:15 daily (cron `15 14/4 * * *`).
      const n = Math.max(1, v.interval);
      const [startHour, startMin] = parseHHMM(v.start_time);
      if (n === 1) return `${startMin} * * * *`;
      if (startHour === 0 && 24 % n === 0) return `${startMin} */${n} * * *`;
      return `${startMin} ${startHour}/${n} * * *`;
    }
    case "daily": {
      if (v.byday.length === 0 || v.byday.length === 7) return `${minute} ${hour} * * *`;
      const dows = v.byday
        .map((c) => DAY_CODE_TO_NUM[c])
        .filter((n): n is number => n !== undefined)
        .sort((a, b) => a - b)
        .join(",");
      return `${minute} ${hour} * * ${dows}`;
    }
    case "weekly": {
      // Anchored to start_date's weekday — single DOW in the cron.
      const dow = DAY_CODE_TO_NUM[dayCodeFromDateStr(v.start_date)] ?? 0;
      return `${minute} ${hour} * * ${dow}`;
    }
    case "monthly": {
      const monthPart = v.interval > 1 ? `*/${v.interval}` : "*";
      if (v.monthly_last_day) {
        return `${minute} ${hour} 28-31 ${monthPart} *`;
      }
      const dom = dayOfMonthFromDate(v.start_date);
      return `${minute} ${hour} ${dom} ${monthPart} *`;
    }
    case "yearly": {
      const dom = dayOfMonthFromDate(v.start_date);
      const mo = monthFromDate(v.start_date);
      return `${minute} ${hour} ${dom} ${mo} *`;
    }
  }
}

// ---------------------------------------------------------------------------
// cron → RecurrenceValue (best-effort form-mode seed)
// ---------------------------------------------------------------------------

interface CronFields {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

function splitCron(cron: string): CronFields | null {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  return { minute: minute!, hour: hour!, dayOfMonth: dayOfMonth!, month: month!, dayOfWeek: dayOfWeek! };
}

function parseScalar(field: string): number | null {
  if (/^\d+$/.test(field)) return parseInt(field, 10);
  return null;
}

/**
 * Returns true if the given cron string can be losslessly represented in
 * form mode. The check is a round-trip equality on the parsed AST.
 */
export function canRoundTripToForm(cron: string): boolean {
  const fields = splitCron(cron);
  if (!fields) return false;

  const seeded = seedRecurrenceFromCron(cron);
  if (!seeded) return false;

  const recompiled = compileRecurrenceToCron(seeded);
  const recompiledFields = splitCron(recompiled);
  if (!recompiledFields) return false;

  return cronFieldsEqual(fields, recompiledFields);
}

function cronFieldsEqual(a: CronFields, b: CronFields): boolean {
  return normalizeField(a.minute) === normalizeField(b.minute)
    && normalizeField(a.hour) === normalizeField(b.hour)
    && normalizeField(a.dayOfMonth) === normalizeField(b.dayOfMonth)
    && normalizeField(a.month) === normalizeField(b.month)
    && normalizeDowField(a.dayOfWeek) === normalizeDowField(b.dayOfWeek);
}

function normalizeField(f: string): string {
  // "0,1,2,3,4,5,6" should be considered equal to "*"
  if (f === "*") return "*";
  if (/^[\d,]+$/.test(f)) {
    const nums = f.split(",").map(Number).sort((a, b) => a - b);
    return nums.join(",");
  }
  return f;
}

function normalizeDowField(f: string): string {
  if (f === "*" || f === "0,1,2,3,4,5,6") return "*";
  return normalizeField(f);
}

/**
 * Best-effort seed of RecurrenceValue from a cron string for form-mode
 * editing. Returns null when the cron uses features form mode can't
 * express (hour ranges, step values, etc.).
 */
export function seedRecurrenceFromCron(cron: string): RecurrenceValue | null {
  const f = splitCron(cron);
  if (!f) return null;

  const pad2 = (n: number) => String(n).padStart(2, "0");

  // Minutely: `* * * * *` | `*/N * * * *` | `M/N * * * *`
  // Captures `*`, step-only, or anchor/step.
  const minMatch = f.minute === "*" ? { m: 0, n: 1 }
    : /^\*\/(\d+)$/.test(f.minute) ? { m: 0, n: parseInt(f.minute.slice(2), 10) || 1 }
    : /^(\d+)\/(\d+)$/.test(f.minute) ? (() => {
        const [, mStr, nStr] = f.minute.match(/^(\d+)\/(\d+)$/)!;
        return { m: parseInt(mStr!, 10), n: parseInt(nStr!, 10) || 1 };
      })()
    : null;
  if (minMatch && f.hour === "*" && f.dayOfMonth === "*" && f.month === "*" && f.dayOfWeek === "*") {
    return {
      ...EMPTY_RECURRENCE,
      is_recurring: true,
      all_day: false,
      granularity: "minutely",
      interval: minMatch.n,
      start_time: `00:${pad2(minMatch.m)}`,
    };
  }

  // Hourly: `M * * * *` | `M */N * * *` | `M H/N * * *` (M is scalar)
  const minScalar = parseScalar(f.minute);
  const hourMatch = f.hour === "*" ? { h: 0, n: 1 }
    : /^\*\/(\d+)$/.test(f.hour) ? { h: 0, n: parseInt(f.hour.slice(2), 10) || 1 }
    : /^(\d+)\/(\d+)$/.test(f.hour) ? (() => {
        const [, hStr, nStr] = f.hour.match(/^(\d+)\/(\d+)$/)!;
        return { h: parseInt(hStr!, 10), n: parseInt(nStr!, 10) || 1 };
      })()
    : null;
  if (minScalar !== null && hourMatch
      && f.dayOfMonth === "*" && f.month === "*" && f.dayOfWeek === "*") {
    return {
      ...EMPTY_RECURRENCE,
      is_recurring: true,
      all_day: false,
      granularity: "hourly",
      interval: hourMatch.n,
      start_time: `${pad2(hourMatch.h)}:${pad2(minScalar)}`,
    };
  }

  const minute = parseScalar(f.minute);
  const hour = parseScalar(f.hour);
  if (minute === null || hour === null) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const start_time = `${pad(hour)}:${pad(minute)}`;

  const base: RecurrenceValue = {
    ...EMPTY_RECURRENCE,
    is_recurring: true,
    all_day: false,
    start_time,
    end_time: start_time,
    end_date: EMPTY_RECURRENCE.start_date,
  };

  // Last-day-of-month: `... 28-31 * *` (or `... 28-31 */N *`)
  if (f.dayOfMonth === "28-31" && (f.month === "*" || /^\*\/\d+$/.test(f.month)) && f.dayOfWeek === "*") {
    const interval = f.month === "*" ? 1 : parseInt(f.month.slice(2), 10) || 1;
    return {
      ...base,
      granularity: "monthly",
      interval,
      monthly_last_day: true,
    };
  }

  // Yearly: `m h D M *` where D and M are scalar
  if (f.dayOfWeek === "*" && parseScalar(f.month) !== null && parseScalar(f.dayOfMonth) !== null) {
    const dom = parseScalar(f.dayOfMonth)!;
    const mo = parseScalar(f.month)!;
    return {
      ...base,
      granularity: "yearly",
      interval: 1,
      // start_date drives display of the day/month for yearly
      start_date: `${EMPTY_RECURRENCE.start_date.slice(0, 4)}-${pad(mo)}-${pad(dom)}`,
    };
  }

  // Monthly: `m h D * *` or `m h D */N *`
  if (f.dayOfWeek === "*" && parseScalar(f.dayOfMonth) !== null && (f.month === "*" || /^\*\/\d+$/.test(f.month))) {
    const dom = parseScalar(f.dayOfMonth)!;
    const interval = f.month === "*" ? 1 : parseInt(f.month.slice(2), 10) || 1;
    return {
      ...base,
      granularity: "monthly",
      interval,
      start_date: `${EMPTY_RECURRENCE.start_date.slice(0, 7)}-${pad(dom)}`,
    };
  }

  // Daily/weekly: `m h * * D|D,D,...|*`
  if (f.dayOfMonth === "*" && f.month === "*") {
    if (f.dayOfWeek === "*") {
      // Every day → daily with all 7 days
      return {
        ...base,
        granularity: "daily",
        interval: 1,
        byday: [...DAY_NUM_TO_CODE],
      };
    }
    if (/^[\d,]+$/.test(f.dayOfWeek)) {
      const dows = f.dayOfWeek.split(",").map(Number);
      const byday = dows
        .map((n) => DAY_NUM_TO_CODE[n])
        .filter((c): c is typeof DAY_NUM_TO_CODE[number] => c !== undefined);
      if (byday.length === 0) return null;
      // Single DOW → weekly (anchored). Multi DOW → daily (with chips).
      if (byday.length === 1) {
        // Adjust start_date so its weekday matches the cron's single DOW.
        const targetDow = DAY_CODE_TO_NUM[byday[0]!]!;
        return {
          ...base,
          granularity: "weekly",
          interval: 1,
          byday: [],
          start_date: shiftDateToDow(EMPTY_RECURRENCE.start_date, targetDow),
        };
      }
      return {
        ...base,
        granularity: "daily",
        interval: 1,
        byday,
      };
    }
  }

  return null;
}

function shiftDateToDow(dateStr: string, targetDow: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
  const currentDow = dt.getUTCDay();
  const diff = (targetDow - currentDow + 7) % 7;
  dt.setUTCDate(dt.getUTCDate() + diff);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
